import { ILaterLead, IMergePreview } from "../models/Contact";
import { IBankChoice, IMergeStep } from "./contactDuplicates";

// The decisions behind the contact page's merge and unmerge dialogs, kept out of the components so
// they can be tested.

// What the merge dialog sends, in order. Contacts on more than one account are refused by the API
// until those accounts are one, so the accounts go first, into the one the preview names - the same
// order the Duplicates page uses.
export function contactMergeSteps(
  preview: Pick<IMergePreview, "contacts" | "accounts" | "accountTargetId">,
  bankDetailsFromAccountId: string | null = null
): { accounts: IMergeStep | null; contacts: IMergeStep | null } {
  const kept = preview.contacts.find((c) => c.isKept);
  const contactSources = preview.contacts.filter((c) => !c.isKept).map((c) => c.id);

  const accountSources = preview.accountTargetId
    ? preview.accounts.filter((a) => a.id !== preview.accountTargetId).map((a) => a.id)
    : [];

  const accounts: IMergeStep | null =
    preview.accountTargetId && accountSources.length > 0
      ? { targetId: preview.accountTargetId, sourceIds: accountSources }
      : null;
  if (accounts && bankDetailsFromAccountId) accounts.bankDetailsFromAccountId = bankDetailsFromAccountId;

  return {
    accounts,
    contacts: kept && contactSources.length > 0 ? { targetId: kept.id, sourceIds: contactSources } : null,
  };
}

// The different sets of bank details among the accounts the merge will join, the kept account's first.
// Only asked about when there is more than one: new leads for this person are paid to the set kept.
export function previewBankChoices(preview: Pick<IMergePreview, "accounts" | "accountTargetId">): IBankChoice[] {
  if (!preview.accountTargetId) return [];

  const ordered = [...preview.accounts].sort((a, b) =>
    a.id === preview.accountTargetId ? -1 : b.id === preview.accountTargetId ? 1 : 0
  );

  const choices: IBankChoice[] = [];
  for (const a of ordered) {
    if (a.bankDetails && !choices.some((c) => c.bankDetails === a.bankDetails)) {
      choices.push({ accountId: a.id, accountNumber: a.accountNumber, bankDetails: a.bankDetails });
    }
  }
  return choices;
}

// The contacts in a merge with one of them made the kept one; the rest keep their order.
export function keepInstead(keptId: string, otherIds: string[], newKeptId: string): { keptId: string; otherIds: string[] } {
  if (newKeptId === keptId || !otherIds.includes(newKeptId)) return { keptId, otherIds };

  return { keptId: newKeptId, otherIds: [keptId, ...otherIds.filter((id) => id !== newKeptId)] };
}

// Adding a contact to the merge: never the kept one, never twice.
export function addToMerge(keptId: string, otherIds: string[], id: string): string[] {
  return id === keptId || otherIds.includes(id) ? otherIds : [...otherIds, id];
}

// Each later lead starts where the API suggests: back with the restored contact if it came in on
// that contact's old id, otherwise staying.
export function suggestedLaterLeads(laterLeads: Pick<ILaterLead, "id" | "suggestMove">[]): string[] {
  return laterLeads.filter((l) => l.suggestMove).map((l) => l.id);
}

export type FieldStatus = "blank" | "same" | "fills" | "differs";

// How one detail on a row being merged in compares with the kept row's, for the merge dialogs:
// "fills" a blank the kept row has (the merge copies it across), "differs" from what the kept row
// already has (the kept row's wins), or the "same". Case and spacing are ignored, as the matching does.
export function fieldStatus(kept: string | null | undefined, value: string | null | undefined): FieldStatus {
  const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  const k = norm(kept);
  const v = norm(value);

  if (!v) return "blank";
  if (!k) return "fills";
  return k === v ? "same" : "differs";
}

export function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}
